import { useState } from 'react';
import type { SoluuDevice, StoredCredentials } from '@soluu/ble-core';

import {
  deriveCredentialDisplay,
  type CredentialDisplay,
} from '../core/storedCredentials.js';
import type { AttemptAnomaly } from '../core/types.js';
import { useDiagnostics } from '../state/DiagnosticsProvider.js';

interface Props {
  device: SoluuDevice;
  /**
   * Called once per field whose characteristic advertised GATT read but whose
   * read failed anyway. `useAttempt.noteAnomaly` consumes this; it is a no-op
   * when no connect attempt is in flight.
   */
  onReadFailureAnomaly?(input: Pick<AttemptAnomaly, 'kind' | 'message'>): void;
}

// Human-facing copy for a characteristic that claimed read support but failed.
// Carries no credential value — safe for both JSON export channels.
const FAILURE_NOTE: Record<'ssid' | 'password', string> = {
  ssid:
    'The WiFi SSID characteristic advertised GATT read support but the read ' +
    'failed — potential firmware bug. Captured for review.',
  password:
    'The WiFi password characteristic advertised GATT read support but the ' +
    'read failed — potential debug-firmware bug. Captured for review.',
};

export function StoredCredentialsReadout({ device, onReadFailureAnomaly }: Props) {
  const diagnostics = useDiagnostics();
  const [result, setResult] = useState<StoredCredentials | null>(null);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Nothing to read if neither characteristic advertises GATT read.
  const nothingReadable = !device.canReadSSID && !device.canReadPassword;
  const hasRead = result !== null;

  const handleRead = async () => {
    setReading(true);
    setError(null);
    try {
      const r = await device.readStoredCredentials();
      setResult(r);
      reportFailure('ssid', r);
      reportFailure('password', r);
    } catch (cause) {
      // readStoredCredentials only throws NOT_CONNECTED; show it benignly.
      setError(
        cause instanceof Error
          ? cause.message
          : 'Could not read stored credentials.',
      );
    } finally {
      setReading(false);
    }
  };

  // A characteristic that claimed `read` but failed the read is a potential
  // firmware bug — record it in both JSON channels (diagnostics always; the
  // LogEntry anomaly when an attempt is active). Neither carries the value.
  const reportFailure = (field: 'ssid' | 'password', r: StoredCredentials) => {
    const readout = r[field];
    if (!readout.readable || !readout.readFailed) return;
    diagnostics.pushEvent({
      attemptId: null,
      source: 'ble',
      type: 'read:unreadable-despite-claim',
      payload: { field, note: FAILURE_NOTE[field] },
    });
    onReadFailureAnomaly?.({
      kind: 'credential-read-failed-despite-claim',
      message: FAILURE_NOTE[field],
    });
  };

  const ssid = deriveCredentialDisplay({
    readout: result?.ssid ?? { readable: device.canReadSSID, value: null, readFailed: false },
    hasRead,
  });
  const password = deriveCredentialDisplay({
    readout:
      result?.password ?? { readable: device.canReadPassword, value: null, readFailed: false },
    hasRead,
  });

  return (
    <div className="stored-credentials">
      <h3>Stored credentials</h3>
      <p className="muted">
        Read back what the device currently has stored. The SSID is readable on
        most firmware; the password only on a debug build. Read-back values are
        shown here only — they are never written to the session log.
      </p>
      <dl className="kv">
        <dt>Stored SSID</dt>
        <dd>{renderValue(ssid)}</dd>
        <dt>Stored password</dt>
        <dd>
          {password.state === 'value' ? (
            <span className="password-row">
              <span>{showPassword ? password.value : '•'.repeat(password.value.length)}</span>
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </span>
          ) : (
            renderValue(password)
          )}
        </dd>
      </dl>
      <button type="button" onClick={() => void handleRead()} disabled={reading || nothingReadable}>
        {reading ? 'Reading…' : 'Read stored credentials'}
      </button>
      {nothingReadable && (
        <p className="muted">
          This firmware exposes neither credential as readable.
        </p>
      )}
      {error && <p className="muted">{error}</p>}
    </div>
  );
}

function renderValue(display: CredentialDisplay) {
  switch (display.state) {
    case 'not-read-yet':
      return <span className="muted">not read yet</span>;
    case 'unavailable':
      return <span className="muted">Not available in this firmware version</span>;
    case 'empty':
      return <span className="muted">(empty)</span>;
    case 'value':
      return display.value;
  }
}
